import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  History,
  Users,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Globe,
  Link2,
  Repeat,
  Ruler,
  Leaf,
  FileUp,
  Lock,
  Workflow
} from 'lucide-react';

const GREENSTITCH_LOGO = 'https://greenstitch.io/wp-content/uploads/2025/05/cropped-greenstitch-logo-1.webp';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'editor', 'approver', 'viewer'] },
  { path: '/emission-factors', label: 'Emission Factors', icon: FileText, roles: ['admin', 'editor', 'approver', 'viewer'] },
  { path: '/custom-mappings', label: 'Custom Mappings', icon: Link2, roles: ['admin', 'editor', 'approver'] },
  { path: '/category-conversions', label: 'Category Conversions', icon: Repeat, roles: ['admin', 'editor', 'approver'] },
  { path: '/unit-conversions', label: 'Unit Conversions', icon: Ruler, roles: ['admin', 'editor', 'approver'] },
  { path: '/lca-impact-factors', label: 'LCA Impact Factors', icon: Leaf, roles: ['admin', 'editor', 'approver'] },
  { path: '/processes', label: 'Processes', icon: Workflow, roles: ['admin', 'editor', 'approver'] },
  { path: '/pending-approvals', label: 'Pending Approvals', icon: ClipboardCheck, roles: ['admin', 'approver'] },
  { path: '/audit-logs', label: 'Audit Logs', icon: History, roles: ['admin', 'editor', 'approver'] },
  { path: '/esg-report-extraction', label: 'ESG Report Extraction', icon: FileUp, roles: ['admin', 'editor', 'approver'] },
  { path: '/users', label: 'User Management', icon: Users, roles: ['admin'] },
];

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, hasRole, isViewer } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Viewers see every menu entry so they know what exists, but only ones
  // their role actually grants (Emission Factors) are clickable — the rest
  // render locked. Other roles keep the existing behavior of only seeing
  // what they have access to.
  const filteredNavItems = isViewer()
    ? navItems.map(item => ({ ...item, locked: !hasRole(item.roles) }))
    : navItems.filter(item => hasRole(item.roles));

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const Icon = item.icon;

    if (item.locked) {
      return (
        <div
          data-testid={`nav-${item.path.replace('/', '')}-locked`}
          title="Not available for the Viewer role"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-gray-400 cursor-not-allowed"
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {(sidebarOpen || mobileMenuOpen) && <span className="font-medium flex-1">{item.label}</span>}
          {(sidebarOpen || mobileMenuOpen) && <Lock className="w-4 h-4 flex-shrink-0" />}
        </div>
      );
    }

    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.path.replace('/', '')}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200 ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {(sidebarOpen || mobileMenuOpen) && <span className="font-medium">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-xl text-gray-900">Atlas</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`lg:hidden fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="p-4 space-y-1">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex fixed top-0 left-0 bottom-0 flex-col bg-white border-r border-gray-200 z-30 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className={`${sidebarOpen ? 'w-9 h-9' : 'w-8 h-8'} bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Globe className={`${sidebarOpen ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
            </div>
            {sidebarOpen && <span className="font-heading font-bold text-xl text-gray-900">Atlas</span>}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={`w-5 h-5 transition-transform duration-300 ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-200">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">{user?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
              {/* Powered by GreenStitch */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-muted-foreground text-center mb-1">Powered by</p>
                <img src={GREENSTITCH_LOGO} alt="GreenStitch" className="h-5 mx-auto opacity-60" />
              </div>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn-collapsed"
              className="w-full"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`min-h-screen pt-16 lg:pt-0 transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        }`}
      >
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
